import type { ReactNode } from 'react';
import type { AppScreen } from '../lib/query.js';
import { Topbar } from './Topbar.js';
import { PageArt } from './PageArt.js';
import { ToastHost } from './ToastHost.js';

/**
 * Global shell rendered once around every screen except Onboarding and
 * StoryHarness (those keep their own minimal chrome).
 *
 * Composition:
 *  - <Topbar>: persistent breadcrumb + nav + theme toggle + sidebar collapse.
 *  - <ToastHost>: bottom-right ephemeral feedback.
 *  - {children}: the active screen.
 */
export interface AppShellProps {
  readonly screen: AppScreen;
  readonly levelName: string | null;
  readonly onHome: () => void;
  readonly onLearn: () => void;
  readonly onOnline: () => void;
  readonly onLeaderboard: () => void;
  readonly onParent: () => void;
  readonly onSettings: () => void;
  readonly children: ReactNode;
}

export function AppShell({ screen, levelName, onHome, onLearn, onOnline, onLeaderboard, onParent, onSettings, children }: AppShellProps) {
  return (
    <div className="relative isolate min-h-screen bg-cream text-ink" data-appdom-scope={screen}>
      <PageArt screen={screen} />
      <Topbar
        screen={screen}
        levelName={levelName}
        onHome={onHome}
        onLearn={onLearn}
        onOnline={onOnline}
        onLeaderboard={onLeaderboard}
        onParent={onParent}
        onSettings={onSettings}
      />
      {children}
      <ToastHost />
    </div>
  );
}
